/**
 * Inline icons. Drawn on a 20-unit grid, 1.4 stroke, currentColor.
 *
 * Deliberately not an icon library: the whole set is six glyphs, and a
 * dependency that ships a thousand of them to save writing six is a poor trade
 * on a PWA that has to be usable on a phone with one bar of signal in a gravel
 * pit. Add to this file rather than reaching for a package.
 */

type IconProps = { size?: number }

function Svg({ size = 18, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

export function SearchIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="9" r="5.25" />
      <path d="m13 13 3.5 3.5" />
    </Svg>
  )
}

export function BellIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10 2.75A4.25 4.25 0 0 0 5.75 7v3.1L4.4 12.6h11.2l-1.35-2.5V7A4.25 4.25 0 0 0 10 2.75Z" />
      <path d="M8.4 15a1.6 1.6 0 0 0 3.2 0" />
    </Svg>
  )
}

/**
 * Edit. Always paired with a visible or screen-reader label naming WHAT is being
 * edited — a pencil alone on an admin screen with several editable blocks is a
 * button whose target you find out by pressing it.
 */
export function PencilIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13.6 3.4a1.9 1.9 0 0 1 2.7 2.7L7.2 15.2 3.5 16.5l1.3-3.7Z" />
      <path d="m12.4 4.6 3 3" />
    </Svg>
  )
}

/** What this role does. The disclosure control, never a decoration. */
export function InfoIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="10" r="7.25" />
      <path d="M10 9v4.25" />
      <path d="M10 6.4v.1" />
    </Svg>
  )
}

export function InstallIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="6.25" y="2.25" width="7.5" height="15.5" rx="1.75" />
      <path d="M9 15.25h2" />
    </Svg>
  )
}

export function StarIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10 3.25 11.9 7.6l4.6.4-3.5 3.05 1.05 4.5L10 13.2l-4.05 2.35L7 11.05 3.5 8l4.6-.4Z" />
    </Svg>
  )
}

/* ---------- the bottom bar's three tabs (SHL-18) ----------

   Engagement Hub, Wiki One and Star One, below 820px. Star One reuses
   StarIcon above — it is already the glyph on every star count in the app, and
   a second star drawn for the tab would be two answers to one question
   (standing rule 11, applied to a drawing rather than a function).

   THE CONETEC MARK IS NOT AVAILABLE FOR ANY OF THESE, and that is the one hard
   constraint on this set. The mark inside the shimmering ring is the AI
   disclosure — a person must be able to tell they are about to ask a machine
   BEFORE they click. Putting the mark on a navigation tab spends the one
   symbol that means "a machine wrote this" on a destination that is entirely
   human, which is the same mistake as painting Wiki One teal.

   Drawn so the three SILHOUETTES differ, not just the details: a pitched roof,
   a hook, and a point. At 24px on a phone in daylight the outline is all
   anybody reads. */

/** Engagement Hub, the `#/` route. A house, because the tab is home and a
 *  house is the one glyph nobody has to learn. No door: at 24px it is three
 *  more strokes inside an already-small shape. */
export function HomeIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.2 9.1 10 3.4l6.8 5.7" />
      <path d="M5.1 7.7v8.2a1 1 0 0 0 1 1h7.8a1 1 0 0 0 1-1V7.7" />
    </Svg>
  )
}

/**
 * Wiki One. A bare question mark.
 *
 * NOT a question mark in a circle: InfoIcon above is a circle with a bar and a
 * dot, and at 24px a circled "?" beside it in the same interface is the same
 * picture. The container is what collides, so the container goes.
 *
 * It reads as "help" as much as "wiki", and that is correct rather than a
 * compromise — HLP-1 puts the one ask box on this route, so the tab genuinely
 * is where a question goes. If that ever stops being true, this glyph should
 * be revisited rather than relabelled.
 */
export function QuestionIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6.9 7.4a3.1 3.1 0 1 1 5.55 1.9L10 11.6v1.2" />
      <path d="M10 15.6v.1" />
    </Svg>
  )
}

/**
 * The ConeTec symbol. THE ACTUAL LOGO — the same path data as
 * `OneConetec Design System/assets/symbol.svg`, not a redrawing.
 *
 * ── WHY THE REAL PATHS AND NOT A SIMPLIFIED GLYPH ───────────────────────────
 *
 * The first version of this was three hand-drawn strokes "evoking" the symbol
 * plus a sparkle. It was unreadable at 15px and, worse, it was a logo nobody
 * had approved — a brand mark redrawn by eye is a different brand mark. These
 * are the real curves, scaled by the viewBox.
 *
 * ── IT IS MONOCHROME currentColor, AND THAT IS THE WHOLE TRICK ──────────────
 *
 * The full-colour symbol is ConeTec blue with a RED cone tip. Both are dropped
 * here, and neither is a design shortcut:
 *
 *   - The mark is used on a BLUE button, so blue-on-blue is invisible and it
 *     has to be white. currentColor makes it white by inheriting, which means
 *     the same component also works in a teal label and on a light card
 *     without a second copy.
 *   - Red is danger and focus everywhere else in this palette. On the control
 *     that hands your question to an AI it would say exactly the wrong thing —
 *     and at 15px the tip is four pixels of detail that turns to mush anyway.
 *
 * DO NOT give this its own fill. The one mark inheriting one colour is what
 * stops the topbar button, the Wiki One button and the answer card's label
 * drifting into three different marks, which is what happened before 8 Sep.
 *
 * ── THE AI DISCLOSURE IS NOT IN THIS GLYPH ──────────────────────────────────
 *
 * There is no sparkle here. The disclosure moved OUT of the mark and into the
 * shimmering rainbow ring, which is CSS (`.airing` / `.aidisc` in hub.css) —
 * so the ring can wrap a button, a bare mark, or anything else, and the logo
 * stays the logo. A sparkle welded into the brand mark was the wrong place for
 * it: it made the ConeTec symbol into an AI symbol, and "One" names the
 * company, not the machine.
 */
export function ConeTecMark({ size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 59.5 58.33"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path transform="matrix(1 0 0 -1 18.229 27.4177)" d="M 0 0 C 5.74 0 8.77 -1.13 11.46 -2.14 C 14.07 -3.12 16.33 -3.96 21.22 -3.96 C 26.12 -3.96 28.38 -3.12 30.99 -2.14 L 31.09 -2.1 C 33.33 -1.27 35.88 -0.32 40.07 -0.07 L 40.28 -0.06 L 40.27 0.16 C 40.1 2.93 39.54 5.65 38.61 8.25 L 38.56 8.39 L -16.46 8.39 L -16.51 8.25 C -17.65 5.12 -18.23 1.79 -18.23 -1.64 C -18.23 -2.39 -18.21 -3.01 -18.16 -3.6 L -18.14 -3.82 L -17.92 -3.8 C -15.07 -3.51 -13.22 -2.81 -11.43 -2.14 C -8.76 -1.13 -5.72 0 0 0" />
      <path transform="matrix(1 0 0 -1 50.9143 34.0034)" d="M 0 0 L -0.05 -0.02 C -2.72 -1.02 -5.74 -2.15 -11.46 -2.15 C -17.16 -2.15 -20.19 -1.02 -22.86 -0.03 L -22.92 -0.01 C -25.53 0.97 -27.79 1.81 -32.68 1.81 C -37.54 1.81 -39.82 0.97 -42.45 -0.01 C -44.36 -0.73 -46.53 -1.56 -49.84 -1.94 L -50.08 -1.97 L -50.02 -2.21 C -49.3 -5.14 -48.11 -7.94 -46.49 -10.52 L -46.42 -10.63 L 3.15 -10.63 L 3.21 -10.52 C 5.51 -6.86 6.93 -2.82 7.43 1.48 L 7.46 1.75 L 7.19 1.72 C 4.01 1.48 2.15 0.79 0 0" />
      <path transform="matrix(1 0 0 -1 8.5917 49.7767)" d="M 0 0 C 5.54 -5.52 12.88 -8.56 20.68 -8.56 C 28.49 -8.56 35.83 -5.52 41.37 0 L 41.74 0.37 L -0.37 0.37 Z" />
      <path transform="matrix(1 0 0 -1 41.555 2.727)" d="M 0 0 C -3.73 1.75 -7.88 2.73 -12.28 2.73 C -16.9 2.73 -21.28 1.65 -25.15 -0.28 L -12.71 -12.71 Z" />
    </svg>
  )
}

/** The old name, kept as an alias for one release so nothing breaks silently.
 *  New code writes ConeTecMark — this glyph is the company's logo and calling
 *  it a "one mark" invited exactly the confusion the ring now resolves. */
export const OneMarkIcon = ConeTecMark

export function PinIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12.2 2.8 17.2 7.8l-2.4.6-3.1 3.1.5 3.3-5.9-5.9 3.3.5 3.1-3.1Z" />
      <path d="m7.3 12.7-3.5 3.5" />
    </Svg>
  )
}

export function PlayIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="10" r="7.25" />
      <path d="M8.4 7.3 13 10l-4.6 2.7Z" fill="currentColor" />
    </Svg>
  )
}

/** 196. The audit log: a clock face with the arrow that turns it back. */
export function HistoryIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.9 10.6A6.25 6.25 0 1 0 5.6 5.6" />
      <path d="M3.6 3.4v2.9h2.9" />
      <path d="M10 6.75V10l2.25 1.5" />
    </Svg>
  )
}

export function LockIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4.25" y="8.75" width="11.5" height="8" rx="1.5" />
      <path d="M7 8.75V6.5a3 3 0 0 1 6 0v2.25" />
    </Svg>
  )
}

/* ---------- player controls ----------
   Drawn heavier than the rest of the set, and deliberately. These sit on a
   video still rather than on a card, so they have to read against whatever
   frame happens to be underneath; a 1.4 stroke disappears against a bright
   sky, which is roughly half the footage. */

export function PauseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="6.25" y="5" width="2.6" height="10" rx="0.8" fill="currentColor" />
      <rect x="11.15" y="5" width="2.6" height="10" rx="0.8" fill="currentColor" />
    </Svg>
  )
}

export function SoundOnIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 8h2.6L10 5v10L6.6 12H4Z" fill="currentColor" />
      <path d="M12.6 7.6a3.4 3.4 0 0 1 0 4.8" />
      <path d="M14.8 5.6a6.4 6.4 0 0 1 0 8.8" />
    </Svg>
  )
}

export function SoundOffIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 8h2.6L10 5v10L6.6 12H4Z" fill="currentColor" />
      <path d="m12.8 8 4 4" />
      <path d="m16.8 8-4 4" />
    </Svg>
  )
}

export function ExpandIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4.5 8V4.5H8" />
      <path d="M12 4.5h3.5V8" />
      <path d="M15.5 12v3.5H12" />
      <path d="M8 15.5H4.5V12" />
    </Svg>
  )
}

/* ---------- the rail's glyphs (4 Oct, collapsible rail) ----------

   The collapsed rail is glyphs only, so every nav destination needs one. The
   three Engagement items already had theirs (above); these are the rest.
   Same brief as the tab bar: SILHOUETTES that differ — two heads, a bag, a
   flag, a tray, bars, rings — because at 18px on navy the outline is all
   anybody reads. None of them is the ConeTec mark; see above for why. */

/** People and roles. Two heads, the second behind. */
export function PeopleIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="7.5" cy="7" r="2.6" />
      <path d="M2.8 16.2c.5-2.6 2.4-4.2 4.7-4.2s4.2 1.6 4.7 4.2" />
      <path d="M12.6 4.6a2.5 2.5 0 0 1 0 4.8" />
      <path d="M14.3 12.2c1.5.5 2.6 1.9 2.9 4" />
    </Svg>
  )
}

/** Crop a photo (FED-66). Two corner brackets crossing — the frame and the
 *  photo under it. */
export function CropIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M5.5 2.5v11a1 1 0 0 0 1 1h11" />
      <path d="M2.5 5.5h11a1 1 0 0 1 1 1v11" />
    </Svg>
  )
}

/** Shop and stock. A carrier bag. */
export function ShopIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4.5 6.8h11l-.8 9.2a1 1 0 0 1-1 .9H6.3a1 1 0 0 1-1-.9Z" />
      <path d="M7.4 8.6V6.2a2.6 2.6 0 0 1 5.2 0v2.4" />
    </Svg>
  )
}

/** Moderation. A flag — the queue is flagged posts, and "flag" is the verb
 *  on the button that fills it. */
export function FlagIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M5 17V3.6" />
      <path d="M5 4.2c3.3-1.6 6 1.6 10 0v6.6c-4 1.6-6.7-1.6-10 0" />
    </Svg>
  )
}

/** The photo room (145). A camera. */
export function CameraIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 7.2a1.2 1.2 0 0 1 1.2-1.2h2.3l1.3-1.8h4.4L13.5 6h2.3A1.2 1.2 0 0 1 17 7.2v7.6a1.2 1.2 0 0 1-1.2 1.2H4.2A1.2 1.2 0 0 1 3 14.8Z" />
      <circle cx="10" cy="10.8" r="2.8" />
    </Svg>
  )
}

/** Support inbox. A tray. */
export function InboxIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.4 11.2 5.3 4.8a1 1 0 0 1 1-.7h7.4a1 1 0 0 1 1 .7l1.9 6.4v4a1 1 0 0 1-1 1H4.4a1 1 0 0 1-1-1Z" />
      <path d="M3.4 11.2h3.8l1 1.9h3.6l1-1.9h3.8" />
    </Svg>
  )
}

/** 337. Help — a lifebuoy: reaching a person. Not the question mark (Wiki One)
 *  and not the mark (a machine); this page is people. */
export function HelpIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="10" r="6.5" />
      <circle cx="10" cy="10" r="2.8" />
      <path d="M5.4 5.4 8 8M14.6 5.4 12 8M5.4 14.6 8 12M14.6 14.6 12 12" />
    </Svg>
  )
}

/** Ask One on the desk — analytics about One's answers, so bars, not the mark. */
export function ChartIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 16.5h13" />
      <path d="M6 13.5v-3" />
      <path d="M10 13.5V6" />
      <path d="M14 13.5V9" />
    </Svg>
  )
}

/** 310. Surveys: a clipboard with two ticked lines - asking a room and
 *  counting what comes back. Not the chart (Ask One's) or the pencil (One
 *  Question's): one picture per destination. */
export function SurveyIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4.5" y="3.5" width="11" height="13.5" rx="1.6" />
      <path d="M8 3.5V2.8h4v.7" />
      <path d="M7 8.2l1.1 1.1L10 7.4" />
      <path d="M11.6 8.4h1.9" />
      <path d="M7 12.6l1.1 1.1L10 11.8" />
      <path d="M11.6 12.8h1.9" />
    </Svg>
  )
}

/** 301. How the hub is used: a pulse line - activity over time, not a bar
 *  chart, because the chart glyph is Ask One's (one picture per destination). */
export function PulseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2.5 10.5h3.2l2-4.5 3.4 8 2-4.5h4.4" />
    </Svg>
  )
}

/** Leads. Rings and a centre: a target. */
export function TargetIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="10" r="6.5" />
      <circle cx="10" cy="10" r="3.2" />
      <path d="M10 10h.01" />
    </Svg>
  )
}

/** Points right when closed; CSS rotates it 90° when its <details> is open.
 *  Also the rail toggle (4 Oct), where CSS mirrors it to point left while the
 *  rail is open — "collapse this way". */
export function ChevronIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="m7.5 4.5 6 5.5-6 5.5" />
    </Svg>
  )
}

/* Thumbs, for rating One's answer only (048). Human answers keep the upvote
   arrow; these never appear beside a person's words. */
export function ThumbUpIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6.5 9.25v7.25H4a.75.75 0 0 1-.75-.75v-5.75A.75.75 0 0 1 4 9.25h2.5Z" />
      <path d="M6.5 9.5 9.6 3.6a1.4 1.4 0 0 1 2.6.9L11.7 8h3.4a1.6 1.6 0 0 1 1.55 2l-1.3 5.2a1.6 1.6 0 0 1-1.55 1.3H6.5" />
    </Svg>
  )
}

export function ThumbDownIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13.5 10.75V3.5H16a.75.75 0 0 1 .75.75V10a.75.75 0 0 1-.75.75h-2.5Z" />
      <path d="M13.5 10.5l-3.1 5.9a1.4 1.4 0 0 1-2.6-.9L8.3 12H4.9a1.6 1.6 0 0 1-1.55-2l1.3-5.2A1.6 1.6 0 0 1 6.2 3.5h7.3" />
    </Svg>
  )
}

/* ---------- the business card (4 Oct) ---------- */

export function MailIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5" width="14" height="10.5" rx="1.5" />
      <path d="m3.6 6 6.4 5 6.4-5" />
    </Svg>
  )
}

export function PhoneIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6.6 3.5H4.9c-.8 0-1.4.7-1.4 1.5.3 6.2 5.3 11.2 11.5 11.5.8 0 1.5-.6 1.5-1.4v-1.7c0-.5-.3-.9-.8-1l-2.4-.8c-.4-.1-.9 0-1.1.3l-.9 1c-1.9-.9-3.4-2.4-4.3-4.3l1-.9c.3-.3.4-.7.3-1.1l-.8-2.4c-.1-.5-.5-.8-1-.8Z" />
    </Svg>
  )
}

export function PlaceIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10 17s5.25-4.6 5.25-8.75a5.25 5.25 0 0 0-10.5 0C4.75 12.4 10 17 10 17Z" />
      <circle cx="10" cy="8.25" r="1.9" />
    </Svg>
  )
}

export function ShareIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10 12.5V3.25" />
      <path d="M6.75 6.25 10 3l3.25 3.25" />
      <path d="M6.5 9H5.25c-.7 0-1.25.55-1.25 1.25v5.5c0 .7.55 1.25 1.25 1.25h9.5c.7 0 1.25-.55 1.25-1.25v-5.5c0-.7-.55-1.25-1.25-1.25H13.5" />
    </Svg>
  )
}

/** Photos → Gallery (10 Oct). A framed landscape: a picture, not a camera. */
export function ImageIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="4" width="14" height="12" rx="1.4" />
      <circle cx="7.3" cy="8.2" r="1.3" />
      <path d="m3.5 14.5 4-4 3 3 2-2 4 3.5" />
    </Svg>
  )
}

/** Photos → Contests (10 Oct). A cup: somebody wins. */
export function TrophyIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6.5 3.5h7v4a3.5 3.5 0 0 1-7 0Z" />
      <path d="M6.5 5H4.2v1.2A2.3 2.3 0 0 0 6.5 8.5" />
      <path d="M13.5 5h2.3v1.2a2.3 2.3 0 0 1-2.3 2.3" />
      <path d="M10 11v3" />
      <path d="M7 16.5h6" />
    </Svg>
  )
}

/** Filters (the gallery's toolbar). Three sliders. */
export function SlidersIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 6h13M3.5 10h13M3.5 14h13" />
      <circle cx="7" cy="6" r="1.6" fill="var(--surface-card, #fff)" />
      <circle cx="13" cy="10" r="1.6" fill="var(--surface-card, #fff)" />
      <circle cx="9" cy="14" r="1.6" fill="var(--surface-card, #fff)" />
    </Svg>
  )
}


/* ---------- the survey screen's dock (315, 25 Oct) ---------- */

export function QrIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="3.5" width="5" height="5" rx="0.8" />
      <rect x="11.5" y="3.5" width="5" height="5" rx="0.8" />
      <rect x="3.5" y="11.5" width="5" height="5" rx="0.8" />
      <path d="M11.5 11.5h2v2M16.5 11.5v0.01M11.5 16.5h0.01M14.5 14.5h2v2h-2z" />
    </Svg>
  )
}

export function CloudIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 8h5M3.5 12h9M10.5 8h6M14.5 12h2M6 16h8" />
    </Svg>
  )
}

export function ListIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M7.5 5.5h9M7.5 10h9M7.5 14.5h9" />
      <path d="M3.75 5.5h.01M3.75 10h.01M3.75 14.5h.01" strokeWidth="2.2" />
    </Svg>
  )
}

export function CloseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="m5 5 10 10M15 5 5 15" />
    </Svg>
  )
}

export function BroadcastIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="10" r="1.6" />
      <path d="M6.6 6.6a4.8 4.8 0 0 0 0 6.8M13.4 6.6a4.8 4.8 0 0 1 0 6.8M4.2 4.2a8.2 8.2 0 0 0 0 11.6M15.8 4.2a8.2 8.2 0 0 1 0 11.6" />
    </Svg>
  )
}

/* 317: ideas shown as pills. */
export function PillsIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2.5" y="4" width="8" height="4.5" rx="2.25" />
      <rect x="12" y="4" width="5.5" height="4.5" rx="2.25" />
      <rect x="2.5" y="11.5" width="5.5" height="4.5" rx="2.25" />
      <rect x="9.5" y="11.5" width="8" height="4.5" rx="2.25" />
    </Svg>
  )
}
